import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  ArrowRight,
  Bell,
  BellOff,
  CheckCheck,
  MoreHorizontal,
  RefreshCw,
  SlidersHorizontal,
  Trash2,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  notificationKeys,
  useDismissAllNotifications,
  useDismissNotification,
  useMarkAllAsRead,
  useMarkAsRead,
  useNotificationsWithRealtime,
} from '../hooks/useNotifications';
import { groupNotificationsByDate, type Notification } from '../types';
import { NotificationInboxTabs, type NotificationInboxView } from './NotificationInboxTabs';
import { NotificationItem } from './NotificationItem';

export interface NotificationCenterProps {
  onSettingsClick?: () => void;
  className?: string;
}

function LoadingSkeleton() {
  return (
    <div aria-label="Loading notifications">
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="flex items-start gap-3 border-b px-4 py-3 last:border-b-0">
          <Skeleton className="h-9 w-9 shrink-0 rounded-md" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-3.5 w-3/5" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-16" />
          </div>
        </div>
      ))}
    </div>
  );
}

function PanelState({
  title,
  description,
  onRetry,
}: {
  title: string;
  description: string;
  onRetry?: () => void;
}) {
  return (
    <div className="flex min-h-56 flex-col items-center justify-center px-5 py-9 text-center">
      <span className="mb-3 flex h-10 w-10 items-center justify-center rounded-md bg-muted">
        <BellOff className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
      </span>
      <p className="text-sm font-semibold">{title}</p>
      <p className="mt-1 max-w-xs text-xs leading-5 text-muted-foreground">{description}</p>
      {onRetry && (
        <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

function NotificationList({
  notifications,
  onMarkAsRead,
  onDismiss,
  onClick,
}: {
  notifications: Notification[];
  onMarkAsRead: (id: string) => void;
  onDismiss: (id: string) => void;
  onClick: (notification: Notification) => void;
}) {
  const grouped = groupNotificationsByDate(notifications);

  return (
    <div role="feed" aria-label="Recent notifications">
      {Array.from(grouped.entries()).map(([date, items]) => (
        <section key={date}>
          <h4 className="sticky top-0 z-10 flex items-center justify-between border-b bg-background/95 px-4 py-1.5 text-xs font-medium text-muted-foreground backdrop-blur">
            <span>{date}</span>
            <span className="font-normal tabular-nums">
              {items.length} {items.length === 1 ? 'update' : 'updates'}
            </span>
          </h4>
          {items.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              compact
              onClick={onClick}
              onMarkAsRead={onMarkAsRead}
              onDismiss={onDismiss}
            />
          ))}
        </section>
      ))}
    </div>
  );
}

export function NotificationCenter({ onSettingsClick, className }: NotificationCenterProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<NotificationInboxView>('all');
  const [confirmDismissAll, setConfirmDismissAll] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { notifications, unreadCount, isLoading, isFetching, isError, refetch, isEnabled } =
    useNotificationsWithRealtime();
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();
  const dismissNotification = useDismissNotification();
  const dismissAllNotifications = useDismissAllNotifications();

  useEffect(
    () => () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    },
    [],
  );

  const refreshNotifications = () => {
    if (!isEnabled) return;
    setIsRefreshing(true);
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => setIsRefreshing(false), 500);
    void refetch();
    void queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen && isEnabled) {
      void refetch();
      void queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.actionUrl) return;
    setOpen(false);
    void navigate({ to: notification.actionUrl } as never);
  };

  const handleSettingsClick = () => {
    setOpen(false);
    onSettingsClick?.();
  };

  const unreadNotifications = notifications.filter((notification) => !notification.read);
  const displayedNotifications = activeTab === 'unread' ? unreadNotifications : notifications;
  const showSpinner = isFetching || isRefreshing;

  return (
    <>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn('relative', className)}
            aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
          >
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <Badge
                variant="destructive"
                className="absolute -right-1 -top-1 h-5 min-w-5 px-1.5 text-xs font-semibold"
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-[27rem] max-w-[calc(100vw-1rem)] overflow-hidden p-0 shadow-xl"
          align="end"
          sideOffset={8}
        >
          <header className="flex items-center justify-between gap-3 border-b px-4 py-3">
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-2">
                <h3 className="truncate text-sm font-semibold">Notifications</h3>
                {unreadCount > 0 && (
                  <Badge variant="secondary" className="h-5 shrink-0 px-2 text-[11px] font-medium">
                    {unreadCount} unread
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={refreshNotifications}
                disabled={!isEnabled || showSpinner}
                aria-label="Refresh notifications"
                title="Refresh notifications"
              >
                <RefreshCw className={cn('h-4 w-4', showSpinner && 'animate-spin')} />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    aria-label="Notification inbox actions"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="z-[100] w-56">
                  <DropdownMenuItem onSelect={handleSettingsClick} disabled={!onSettingsClick}>
                    <SlidersHorizontal className="mr-2 h-4 w-4" />
                    Notification settings
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => markAllAsRead.mutate()}
                    disabled={unreadCount === 0 || markAllAsRead.isPending}
                  >
                    <CheckCheck className="mr-2 h-4 w-4" />
                    Mark all as read
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => setConfirmDismissAll(true)}
                    disabled={notifications.length === 0 || dismissAllNotifications.isPending}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Dismiss all notifications
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          <div className="px-4">
            <NotificationInboxTabs
              value={activeTab}
              unreadCount={unreadCount}
              onValueChange={setActiveTab}
              compact
            />
          </div>

          <ScrollArea className="h-[min(25rem,calc(100vh-11rem))] min-h-56">
            {isLoading ? (
              <LoadingSkeleton />
            ) : isError ? (
              <PanelState
                title="Notifications could not be loaded"
                description="Check your connection and try again."
                onRetry={refreshNotifications}
              />
            ) : displayedNotifications.length === 0 ? (
              <PanelState
                title={activeTab === 'unread' ? 'No unread notifications' : 'No notifications yet'}
                description={
                  activeTab === 'unread'
                    ? 'New updates will appear here as they arrive.'
                    : 'Updates from your work will appear here.'
                }
              />
            ) : (
              <NotificationList
                notifications={displayedNotifications}
                onMarkAsRead={(id) => markAsRead.mutate(id)}
                onDismiss={(id) => dismissNotification.mutate(id)}
                onClick={handleNotificationClick}
              />
            )}
          </ScrollArea>

          <footer className="border-t px-2 py-1.5">
            <Button
              variant="ghost"
              className="h-9 w-full justify-between px-2.5 text-sm font-medium"
              onClick={() => {
                setOpen(false);
                void navigate({ to: '/notifications' });
              }}
            >
              Open notification inbox
              <ArrowRight className="h-4 w-4" />
            </Button>
          </footer>
        </PopoverContent>
      </Popover>

      <ConfirmDialog
        open={confirmDismissAll}
        onOpenChange={setConfirmDismissAll}
        title="Dismiss all notifications?"
        description="This removes every notification from your inbox. The activity that created each notification is not deleted."
        confirmText="Dismiss all"
        variant="destructive"
        onConfirm={async () => {
          await dismissAllNotifications.mutateAsync();
        }}
      />
    </>
  );
}

export default NotificationCenter;
